// ── 출퇴근 관리 페이지 어셈블리 ──────────────────────────────────────────────
import React, { useState } from 'react';
import { toDateString, nowKST } from '../../../utils/date';
import { useToast } from '../../../components/Toast';
import Modal from '../../../components/Modal';

import { AttendanceStatusFilters } from './AttendanceStatusFilters';
// 구글시트 연동 잔재 제거 (2026-09-01, 사용자 승인) — 2026-04-26 UI 숨김 후에도 남아 있던
// URL 조회·동기화 핸들러·보존 컴포넌트 삭제. 연차 관리의 시트 기능은 현역이라 그쪽은 무변경.
import { pageWrapStyle } from './styles';
import { MOBILE_BREAKPOINT } from './utils';
// 직원 관리 기준 디자인 표준 토큰 (2026-08-26 통일)
import {
  stdBlackBtn, stdOutlineBtn, stdTabGroup, stdTabBtn,
} from '../adminlayout/designStandard';
// 윗부분 틀은 달력 탭과 같이 쓴다 / 엑셀·시간 설정도 달력 탭과 같은 한 벌 (2026-09-29)
import AttendancePageHeader from '../attendancehub/AttendancePageHeader';
// 3줄 가운데 [‹ 2026년 10월 ›][오늘] — 달력 탭과 같은 부품(2026-10-13)
import AttendanceMonthNav from '../attendancehub/AttendanceMonthNav';
import { useAttendanceTools } from './useAttendanceTools';
import AttendanceMenu from '../attendancehub/AttendanceMenu';
import { useIpMenu } from '../attendancehub/useIpMenu';
import { useOverrideRegister } from '../attendancecalendar/useOverrideRegister';
import { useAttendanceData }  from './useAttendanceData';
import { useAvailableMonths } from './useAvailableMonths';
import { AttendanceFilterPopover } from './AttendanceFilterPopover';
import { useAttendanceEdit }  from './useAttendanceEdit'; // 편집 모달 로직 분리 (300줄 룰)
// MobileList/DesktopTable(TableViews)는 좌우 분할 레이아웃으로 대체됨 (Phase 1-2 후속). 파일 유지
import { AttendanceMobileSummary } from './AttendanceMobileSummary'; // Phase 1-2 B
import { AttendanceDesktopLayout } from './AttendanceDesktopLayout'; // Phase 1-2 후속 — 데스크탑 좌우 분할
import { AttendanceEditForm }        from './AttendanceEditForm';
// HeaderActionBtn — 연차 관리와 공유되는 옵션 C 버튼. 출퇴근은 디자인 표준(designStandard) 버튼으로 교체 (2026-08-26)
// 통합 메뉴 회사 칩(CompanyFilterChips)은 필터 창 안으로 옮겼다(2026-09-30, attendancehub/CompanyFilterField)

/**
 * 출퇴근 관리 페이지 (어셈블리)
 * Props: pageTitle / attendancePath(기본 '/api/admin/attendance')
 *        / companyCode('PINE','DOT','LNJ'…) / apiBasePath(외부회사 API base 예: '/api/admin/ext/DOT')
 */
function AttendanceManagement({
  pageTitle = '출퇴근 관리',
  subtitle = '직원 출퇴근 기록을 조회·수정합니다.',
  // 출퇴근 관리 탭 화면(attendancehub, 2026-09-29)이 제목 줄 아래에 끼워 넣는 [달력][직원별 상세] 탭. 없으면 안 그린다.
  tabSlot = null,
  // 달력 탭에서 이름을 눌러 왔을 때: 그 직원·그 달(YYYY-MM)을 골라 둔 채로 연다. 없으면 예전 그대로.
  initialUserId = null,
  initialYm = null,
  // 탭 화면(2026-09-29)이 주는 값: 예외 스케줄 등록에 쓸 달력 주소, 저장 뒤 달력도 다시 불러오라는 알림
  overrideApiBase = null,
  onDataChanged = null,
  attendancePath = '/api/admin/attendance',
  companyCode = 'PINE',
  apiBasePath = '/api',
  // 통합 모드 (Phase 1-2) — 회사 필터 칩. 3개 모두 전달되어야 렌더됨. 파인/ext는 미전달.
  companyCodes,
  selectedCompanyCodes,
  setSelectedCompanyCodes,
  timeSettingsPathBuilder,
  // ☰ 'IP 관리' 범위(2026-10-23) — 탭 화면 입구가 준다(본사·회사·통합). 없으면 항목 없음
  ipScope = null,
} = {}) {
  const { showToast } = useToast();

  // ── 데이터 + 필터 + 페이지 훅 ────────────────────────────────────────────
  // selectedUserId state — useAttendanceData에 전달해야 하므로 먼저 선언 (아래 모달 state보다 먼저)
  //   달력에서 고른 직원: PC 는 왼쪽 목록에서 선택, 휴대폰은 그 카드를 펼친다(휴대폰 화면은 선택 개념이 없어
  //   selectedUserId 를 두면 위 배지만 그 사람 숫자가 되어 목록과 어긋났다 — 검토에서 발견).
  const focusUserId = Number(initialUserId) > 0 ? Number(initialUserId) : null;
  const [selectedUserId, setSelectedUserId] = useState(() => (
    focusUserId && window.innerWidth > MOBILE_BREAKPOINT ? focusUserId : null));
  const [initialYear, initialMonth] = /^\d{4}-\d{2}$/.test(String(initialYm || '')) ? String(initialYm).split('-').map(Number) : [];
  const {
    year, setYear, month, setMonth,
    nameQuery, setNameQuery, ipFilter, setIpFilter,
    activeStatusFilters, setActiveStatusFilters, statusCounts, userUnfilteredCounts,
    includeResigned, setIncludeResigned, // 재직중/전체 토글 (Phase 1-2 후속)
    ips, holidays, leaveByDate,
    // currentPage/filteredRows/pagedRows/totalPages — 구 데스크탑 테이블/페이지네이션 전용 (Phase 1-2 후속 좌우 분할로 대체)
    isMobile,
    groupedByUser, ipFilterOptions, selectedIpOption, // Phase 1-2 B: groupedByUser=모바일 직원별
    fetchAll,
  } = useAttendanceData({
    attendancePath, showToast, selectedCompanyCodes, selectedUserId, initialYear, initialMonth,
    // 달력은 그달 퇴사(예정)자도 보여 준다 — 그 사람을 눌러 왔으면 '전체'로 열어야 목록에 있다(검토에서 발견).
    initialIncludeResigned: Boolean(focusUserId),
  });

  // 년/월 셀렉트 옵션 — 데이터가 있는 년·월만 (실패 시 기존 ±1년 폴백)
  const { yearOptions, monthsForYear } = useAvailableMonths({ attendancePath, includeResigned });

  // 달 넘기기 — 위 [‹ 2026년 10월 ›][오늘] (2026-10-13 사용자 "출퇴근 달력처럼"). 필터 창의 년/월 고르기와 같은 달 값을 쓴다
  //   (같은 날 사용자 "필터로 8월 선택시 상단도 바뀌면 되잖아"). 달력처럼 기록이 없는 달로도 넘어간다(빈 화면).
  //   [오늘] = 이번 달(nowKST — new Date 직접 사용 금지). 위에서 넘긴 적이 있으면 필터 창 고르기 칸에도 그 달을 보여 준다.
  const [monthNavUsed, setMonthNavUsed] = useState(false);
  const moveMonth = (delta) => {
    const base = (year * 12) + (month - 1) + delta;
    setYear(Math.floor(base / 12));
    setMonth((base % 12) + 1);
    setMonthNavUsed(true);
  };
  const goThisMonth = () => {
    const n = nowKST();
    setYear(n.getUTCFullYear());
    setMonth(n.getUTCMonth() + 1);
    setMonthNavUsed(true);
  };

  // selectedDate — 우측 캘린더 날짜 선택 (selectedUserId는 위 hook 호출 전 선언됨)
  const [selectedDate, setSelectedDate] = useState(null);
  const todayYmd = toDateString(new Date()) || '';
  // [엑셀 다운로드]·[시간 설정] — 달력 탭과 같은 한 벌(useAttendanceTools, 2026-09-29 분리)
  const tools = useAttendanceTools({
    attendancePath, companyCode, apiBasePath, companyCodes, selectedCompanyCodes, timeSettingsPathBuilder, ips,
  });

  // 여기서 기록·예외 스케줄을 저장하면: 이 목록 다시 불러오기 + 탭 화면에 알려 달력도 다시 불러오게
  const refreshAll = () => { fetchAll(); if (onDataChanged) onDataChanged(); };
  // 예외 스케줄 등록 — 달력 탭과 같은 창을 이 탭에서 바로 연다(탭 화면이 달력 주소를 줄 때만, 2026-09-29)
  const overrideReg = useOverrideRegister({ calendarApiBase: overrideApiBase, onSaved: refreshAll });
  // IP 관리 창 — 닫으면 IP 거르기 목록도 다시 불러온다
  const ipMenu = useIpMenu(ipScope, fetchAll);

  // ── 편집 모달 (상태·openEdit·saveEdit·patchTimeForm 분리: useAttendanceEdit, 300줄 룰) ──
  const {
    editing, setEditing, form, setForm, patchTimeForm,
    breakManuallyEdited, setBreakManuallyEdited, editingRegisteredIps,
    openEdit, saveEdit,
  } = useAttendanceEdit({ attendancePath, ips, showToast, fetchAll: refreshAll });

  // ── 렌더 ──────────────────────────────────────────────────────────────────
  // 윗부분은 달력 탭과 같은 틀(AttendancePageHeader, 2026-09-29 "탭 바꿀 때 폼이 달라 왔다갔다 — 통일"):
  //   1줄 제목 … [달력|직원별 상세][☰] / 2줄 [이름 검색][필터] / 3줄 상태 배지 … [재직중|전체](달력의 월별|주별 자리)
  return (
    <div style={pageWrapStyle}>
      <AttendancePageHeader
        isMobile={isMobile}
        title={pageTitle}
        subtitle={subtitle}
        // 통합 메뉴 회사 선택은 제목 아래 칩이었다가 필터 창 안 "회사" 칸으로 (2026-09-30 사용자 ①, 달력 탭과 같게)
        // ☰ 메뉴 — 달력 탭과 같은 항목·순서(사용자: 예외 스케줄 등록 → 시간 설정 → 엑셀 다운로드 → IP 관리)
        menu={(
          <AttendanceMenu items={[
            overrideReg.available && { key: 'override', label: '예외 스케줄 등록', onClick: overrideReg.open },
            { key: 'time', label: '시간 설정', onClick: tools.openTimeSettings },
            { key: 'excel', label: '엑셀 다운로드', onClick: () => tools.exportExcel({ year, month, ipFilter, selectedIpOption, nameQuery }) },
            ipMenu.item,
          ]} />
        )}
        tabSlot={tabSlot}
        searchValue={nameQuery}
        onSearchChange={setNameQuery}
        filterButton={(
          // 필터 팝오버 (분리: AttendanceFilterPopover.jsx) — X 버튼 = 필터 해제
          <AttendanceFilterPopover
            isMobile={isMobile}
            ipFilter={ipFilter} setIpFilter={setIpFilter} ipFilterOptions={ipFilterOptions}
            year={year} setYear={setYear} month={month} setMonth={setMonth}
            yearOptions={yearOptions} monthsForYear={monthsForYear}
            presetMonth={Boolean(initialYear)}
            monthShown={monthNavUsed}
            companyFilter={setSelectedCompanyCodes
              ? { companyCodes, selected: selectedCompanyCodes, onChange: setSelectedCompanyCodes } : null}
          />
        )}
        // 3줄: 왼쪽 [재직중|전체](달력의 월별|주별 자리) · 가운데 [‹ 2026년 10월 ›][오늘](2026-10-13, 달력과 같은 부품) ·
        //   오른쪽 상태 배지(달력의 범례 자리) — 2026-09-29 사용자 확정
        toolbarLeft={(
          <div style={stdTabGroup}>
            <button style={stdTabBtn(!includeResigned)} onClick={() => setIncludeResigned(false)}>재직중</button>
            <button style={stdTabBtn(includeResigned)} onClick={() => setIncludeResigned(true)} title="전체(퇴사자 포함)">전체</button>
          </div>
        )}
        toolbarCenter={(
          <AttendanceMonthNav isMobile={isMobile} year={year} month={month} onMoveMonth={moveMonth} onToday={goThisMonth} />
        )}
        toolbarRight={(
          // 빠른 필터 배지 — 카운트는 타입 필터 제외. 직원 선택 시 그 직원 카운트, 미선택 시 전체.
          <AttendanceStatusFilters
            activeFilters={activeStatusFilters}
            counts={userUnfilteredCounts || statusCounts}
            onToggle={(k) => setActiveStatusFilters((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k])}
          />
        )}
      />

      {/* 모바일: 직원별 아코디언 / 데스크탑: 좌우 분할 (직원 리스트 + 캘린더 + 상세) — Phase 1-2 후속 */}
      {isMobile ? (
        <AttendanceMobileSummary groupedByUser={groupedByUser} onEdit={openEdit} focusUserId={focusUserId} />
      ) : (
        <AttendanceDesktopLayout
          groupedByUser={groupedByUser} year={year} month={month}
          selectedUserId={selectedUserId} setSelectedUserId={setSelectedUserId}
          selectedDate={selectedDate} setSelectedDate={setSelectedDate}
          todayYmd={todayYmd} holidays={holidays} leaveByDate={leaveByDate} onEdit={openEdit}
        />
      )}

      {/* 출퇴근 수정 모달 */}
      <Modal
        open={Boolean(editing)}
        icon="✏️"
        title={editing ? `출퇴근 수정 - ${editing.name}` : '출퇴근 수정'}
        onClose={() => setEditing(null)}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button style={stdOutlineBtn} onClick={() => setEditing(null)}>취소</button>
            <button style={stdBlackBtn} onClick={saveEdit}>저장</button>
          </div>
        )}
      >
        <AttendanceEditForm
          form={form} setForm={setForm} patchTimeForm={patchTimeForm}
          breakManuallyEdited={breakManuallyEdited} setBreakManuallyEdited={setBreakManuallyEdited}
          editingRegisteredIps={editingRegisteredIps} editingRow={editing}
        />
      </Modal>

      {/* 모달 영역: 시간 설정(C) — useAttendanceTools 가 그린다(회사 고르기 창 포함) */}
      {tools.modals}
      {/* 예외 스케줄 등록 창 (달력 탭과 같은 창) */}
      {overrideReg.modal}
      {/* IP 관리 창 (☰ 'IP 관리', 2026-10-23) */}
      {ipMenu.modal}
    </div>
  );
}

export default AttendanceManagement;
